import React from 'react'
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  HStack,
  VStack,
  Text,
  Spinner,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FiChevronDown, FiGlobe } from 'react-icons/fi'
import { useLanguage } from '@/contexts/LanguageContext'

interface LanguageSwitcherProps {
  variant?: 'full' | 'compact' | 'icon'
  size?: 'sm' | 'md' | 'lg'
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'full',
  size = 'md',
}) => {
  const { currentLanguage, availableLanguages, changeLanguage, isLoading, isLanguageLocked } =
    useLanguage()

  // Responsive variant - show compact on mobile, full on desktop
  const responsiveVariant =
    useBreakpointValue({
      base: 'compact',
      md: variant,
    }) || variant

  const currentLang = availableLanguages.find((lang) => lang.code === currentLanguage)

  // The organization enforces a language — users cannot change it
  if (isLanguageLocked) {
    return null
  }

  const handleLanguageChange = async (languageCode: string) => {
    await changeLanguage(languageCode as any)
  }

  // Icon-only variant for mobile/compact layouts
  if (responsiveVariant === 'icon') {
    return (
      <Menu>
        <MenuButton
          as={IconButton}
          icon={isLoading ? <Spinner size="sm" /> : <FiGlobe />}
          variant="ghost"
          size={size}
          isLoading={isLoading}
          aria-label="Change language"
        />
        <MenuList>
          {availableLanguages.map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              bg={language.code === currentLanguage ? 'blue.50' : 'transparent'}
              _hover={{ bg: 'blue.50' }}
            >
              <HStack spacing={3}>
                <Text fontSize="lg">{language.flag}</Text>
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" fontWeight="medium">
                    {language.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {language.nativeName}
                  </Text>
                </VStack>
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    )
  }

  // Compact variant - globe icon only
  if (responsiveVariant === 'compact') {
    return (
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={isLoading ? <Spinner size="sm" /> : <FiChevronDown />}
          variant="ghost"
          size={size}
          isLoading={isLoading}
        >
          <FiGlobe />
        </MenuButton>
        <MenuList>
          {availableLanguages.map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              bg={language.code === currentLanguage ? 'blue.50' : 'transparent'}
              _hover={{ bg: 'blue.50' }}
            >
              <HStack spacing={3}>
                <Text fontSize="lg">{language.flag}</Text>
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" fontWeight="medium">
                    {language.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {language.nativeName}
                  </Text>
                </VStack>
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    )
  }

  // Full variant - flag + full language name
  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={isLoading ? <Spinner size="sm" /> : <FiChevronDown />}
        variant="ghost"
        size={size}
        isLoading={isLoading}
      >
        <HStack spacing={2}>
          <Text fontSize="md">{currentLang?.flag}</Text>
          <Text fontSize="sm" fontWeight="medium">
            {currentLang?.name}
          </Text>
        </HStack>
      </MenuButton>
      <MenuList>
        {availableLanguages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            bg={language.code === currentLanguage ? 'blue.50' : 'transparent'}
            _hover={{ bg: 'blue.50' }}
          >
            <HStack spacing={3}>
              <Text fontSize="lg">{language.flag}</Text>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="medium">
                  {language.name}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {language.nativeName}
                </Text>
              </VStack>
            </HStack>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  )
}

export default LanguageSwitcher
